import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatDateToShortDotV2,
  formatTime,
  getTimeColorClass,
  BaseButton,
  STATUS_BADGE_CLASS,
} from '@repo/common';
import { ReactComponent as ArrowRightIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';

const PaymentsRow = ({ item, onExpire, $isMobile }) => {
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState(() => {
    if (!item.expiresAt) return 0;
    const diffSeconds = Math.floor(
      (new Date(item.expiresAt).getTime() - new Date().getTime()) / 1000,
    );
    return diffSeconds > 0 ? diffSeconds : 0;
  });

  const isPending = item.status === 'HOLD' || item.status === 'OFFERED';

  // 계좌이체 예약은 Eximbay 결제 페이지가 아니라 입금 안내 페이지로 보낸다
  const completePaymentPath =
    item.paymentProvider === 'BANK_TRANSFER' && item.orderId
      ? `/community/kpathport/bank-transfer?orderId=${item.orderId}`
      : `/community/kpathport/payments?bookingId=${item.bookingId}`;
  // 계좌 안내까지 간 예약(결제 레코드 있음)은 "안내 보기", 아직 Pay now 전이면 "이어서 진행"
  const completePaymentLabel =
    item.paymentProvider === 'BANK_TRANSFER'
      ? item.orderId
        ? 'View transfer details'
        : 'Continue booking'
      : 'Complete payment';

  useEffect(() => {
    if (!isPending || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onExpire) onExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isPending, timeLeft, onExpire]);

  const displayInvoiceNo = item.invoiceNo?.replace('order_', '') || '-';
  const formattedAmount = item.amount
    ? Number(item.amount).toLocaleString()
    : '0';
  // KRW 예약(계좌이체)은 "55,000 KRW", 그 외는 기존대로 "$"
  const amountSuffix =
    String(item.currency || '').toUpperCase() === 'KRW' ? 'KRW' : '$';
  const formattedStatus = item.status
    ? item.status === 'ATTENDED' || item.status === 'ABSENT'
      ? 'Confirmed'
      : item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase()
    : '-';

  // 환불을 요청만 한 상태는 "Refunded" 가 아니라 "Refund requested" 로 — 완료된 것으로 오해하지 않게
  const refundPending =
    item.status === 'REFUNDED' &&
    ['REQUESTED', 'PROCESSING'].includes(String(item.refundStatus || ''));
  const badgeLabel = isPending
    ? 'Pending'
    : refundPending
      ? 'Refund requested'
      : formattedStatus;
  const badgeClass =
    STATUS_BADGE_CLASS[badgeLabel] ?? 'bg-grayscale-gray02 text-text-secondary';

  if ($isMobile) {
    return (
      <div className='flex flex-col w-full bg-grayscale-gray00 rounded-[15px] p-[14px] gap-2 shadow-2'>
        {/* 상태 + 날짜 */}
        <div className='flex items-center justify-between'>
          <span
            className={`px-2 py-[1px] rounded-md typo-b11Strong ${badgeClass}`}
          >
            {badgeLabel}
          </span>
          <span className='typo-b12 text-text-secondary'>
            {item.date ? formatDateToShortDotV2(item.date) : '-'}
          </span>
        </div>

        {/* 타이틀 */}
        <span className='typo-b13Strong text-text-primary'>{item.item}</span>

        {/* 인보이스 + 금액 */}
        <div className='flex items-center justify-between gap-2'>
          <span
            className='typo-b11 text-text-secondary truncate'
            title={item.invoiceNo}
          >
            {displayInvoiceNo ? `${displayInvoiceNo}` : '-'}
          </span>
          <span className='typo-b14Strong text-text-primary whitespace-nowrap'>
            {formattedAmount} {amountSuffix}
          </span>
        </div>

        {/* 구분선 */}
        <div className='w-full h-px bg-grayscale-gray02' />

        {/* 타이머 + 액션 */}
        <div
          className={`flex items-center ${isPending ? 'justify-between' : 'justify-start'}`}
        >
          {isPending ? (
            <span className={`typo-b11Strong ${getTimeColorClass(timeLeft)}`}>
              {formatTime(timeLeft)} left
            </span>
          ) : (
            <span />
          )}

          {isPending ? (
            <button
              onClick={() => navigate(completePaymentPath)}
              className='flex items-center justify-center gap-0.5 h-[30px] typo-b12Strong text-green-deep03'
            >
              {completePaymentLabel}
              <ArrowRightIcon className='w-4 h-4' />
            </button>
          ) : (
            <button
              onClick={() =>
                navigate(`/mypage?tab=Payments&invoice=${item.invoiceNo}`)
              }
              className='flex items-center justify-center gap-0.5 h-[26px] typo-b12Strong text-text-primary'
            >
              View invoice
              <ArrowRightIcon className='w-4 h-4' />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='flex w-full h-[48px] items-center bg-grayscale-gray00'>
      {/* 1. Invoice # */}
      <div
        className='flex justify-center items-center p-1 flex-shrink-0'
        style={{ width: '130px' }}
      >
        <span
          className='typo-b14 text-text-secondary block w-full text-center truncate'
          title={item.invoiceNo}
        >
          {displayInvoiceNo}
        </span>
      </div>

      {/* 2. Date */}
      <div
        className='flex justify-center items-center p-1 flex-shrink-0'
        style={{ width: '120px' }}
      >
        <span className='typo-b14 text-text-secondary'>
          {item.date ? formatDateToShortDotV2(item.date) : '-'}
        </span>
      </div>

      {/* 3. Item */}
      <div className='flex justify-center items-center p-1 flex-1 min-w-0'>
        <span className='typo-b14Strong text-text-primary truncate'>
          {item.item}
        </span>
      </div>

      {/* 4. Amount */}
      <div
        className='flex justify-center items-center p-1 flex-shrink-0'
        style={{ width: '100px' }}
      >
        <span className='typo-b14 text-text-secondary'>
          {formattedAmount} {amountSuffix}
        </span>
      </div>

      {/* 5. Status */}
      <div
        className='flex flex-col justify-center items-center p-1 flex-shrink-0 typo-b14 text-text-secondary'
        style={{ width: '100px' }}
      >
        {isPending ? (
          <>
            <span className='truncate'>Pending</span>
            <span className={`${getTimeColorClass(timeLeft)}`}>
              {formatTime(timeLeft)}
            </span>
          </>
        ) : (
          <span className='typo-b14 text-text-secondary'>
            {formattedStatus}
          </span>
        )}
      </div>

      {/* 6. Receipt */}
      <div
        className='flex justify-center items-center p-1 flex-shrink-0'
        style={{ width: '160px' }}
      >
        {!isPending ? (
          <BaseButton
            type='text_basic'
            size='s'
            onClick={() =>
              navigate(`/mypage?tab=Payments&invoice=${item.invoiceNo}`)
            }
            hideIcon
          >
            View invoice
          </BaseButton>
        ) : (
          <BaseButton
            type='text_basic'
            size='s'
            onClick={() => navigate(completePaymentPath)}
            width='136px'
            hideIcon
          >
            {completePaymentLabel}
          </BaseButton>
        )}
      </div>
    </div>
  );
};

export default PaymentsRow;
